import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { TestBem } from '../../service/TestBem';
import { getUserProfileConditionTitle } from '../../service/UserProfileHelpers/UserProfileHelpers';
import { DisplayConditions as DisplayConditionsTypings } from '../../typings/bannersEditState';
import { LinkTarget } from '../../typings/common';
import { CopyTooltipStatus } from '../../typings/domain';
import { UserProfileCondition } from '../../typings/userProfileRetargeting';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { ExpandableText } from '../ExpandableText/ExpandableText';
import { Shape2, Shape2Icon, Shape2Theme } from '../Shape2/Shape2';
import { Skeleton } from '../Skeleton/Skeleton';
import { StyledLink } from '../StyledLink/StyledLink';
import { ScopedTooltip } from '../TooltipDecorator/ScopedTooltip';

import './DisplayConditions.css';
import * as keyset from './DisplayConditions.i18n';

const i18n = i18nFactory(keyset);
const BLOCK = 'display-conditions';

const USER_PROFILE_SHORT_VIEW_LENGTH = 4;
const RETARGETING_SHORT_VIEW_LENGTH = 4;

export enum KeywordsTextTypeView {
    vertical = 'vertical',
    horizontal = 'horizontal'
}

export interface DisplayConditionsProps extends DisplayConditionsTypings {
    isUserProfileEnabled?: boolean;
    isRetargetingEnabled?: boolean;
    isKeywordsEnabled?: boolean;
    isScreensEnabled?: boolean;
    editGroupLink: string;
    onCopyClick: (text: string) => void;
    onChangeTextTypeView: (keywordsTextTypeView: KeywordsTextTypeView) => void;
    onControlClick: () => void;
    cls?: string;
}

export class DisplayConditions extends React.PureComponent<DisplayConditionsProps> {
    private tooltipRefs = {};
    private clickHandlers = {};

    render() {
        const { isOpen, cls, onControlClick } = this.props;

        return (
            <div className={classNames(Bem.cls(BLOCK), cls)}>
                <div
                    className={classNames(Bem.cls(BLOCK, 'main-header'), TestBem.cls(BLOCK, 'main-header'))}
                    onClick={onControlClick}
                >
                    <div className={Bem.cls(BLOCK, 'main-title')}>{i18n('title')}</div>
                    <Shape2
                        cls={Bem.cls({
                            block: BLOCK,
                            elem: 'control-element',
                            mods: {
                                iconType: Shape2Icon.verticalArrow,
                                opened: isOpen ? 'yes' : 'no'
                            }
                        })}
                        hoverable={true}
                        theme={Shape2Theme.default}
                        icon={Shape2Icon.verticalArrow}
                        width={14}
                        height={14}
                    />
                </div>
                {isOpen ? this.getContent() : this.getDescription()}
            </div>
        );
    }

    private getDescription = () => {
        const { isUserProfileEnabled, isRetargetingEnabled, isKeywordsEnabled, isScreensEnabled } = this.props;

        const text = [
            isRetargetingEnabled && i18n('description-retargetings'),
            isUserProfileEnabled && i18n('description-user-profile'),
            isKeywordsEnabled && i18n('description-keywords'),
            isScreensEnabled && i18n('description-screens')
        ]
            .filter(Boolean)
            .join(', ');

        return <div className={Bem.cls(BLOCK, 'description')}>{text[0].toUpperCase() + text.slice(1)}</div>;
    };

    private getContent = () => {
        const {
            editGroupLink,
            isLoading,
            isUserProfileEnabled,
            isRetargetingEnabled,
            isKeywordsEnabled,
            isScreensEnabled
        } = this.props;

        return (
            <>
                {isLoading ? (
                    <div className={Bem.cls(BLOCK, 'skeleton')}>
                        {isKeywordsEnabled && this.getSkeleton()}
                        {isRetargetingEnabled && this.getSkeleton()}
                        {isUserProfileEnabled && this.getSkeleton()}
                        {isScreensEnabled && this.getSkeleton()}
                    </div>
                ) : (
                    <div
                        className={classNames(
                            Bem.cls(BLOCK, 'content-container'),
                            TestBem.cls(BLOCK, 'content-container')
                        )}
                    >
                        {this.getKeywordsContent()}
                        {this.getRetargetingContent()}
                        {this.getUserProfileContent()}
                        {this.getScreensContent()}
                        <StyledLink target={LinkTarget.blank} href={editGroupLink}>
                            <ButtonDecorator
                                cls={Bem.cls(BLOCK, 'go-to-edit-group-button')}
                                theme={'pseudo'}
                                text={i18n('go-to-edit-group')}
                                size={'n'}
                                tone="default"
                                view="default"
                            />
                        </StyledLink>
                    </div>
                )}
            </>
        );
    };

    private getKeywordsContent = () => {
        const { keywords, isKeywordsEnabled } = this.props;

        if (!isKeywordsEnabled) {
            return null;
        }

        const KEY = 'keywords';

        this.clickHandlers[KEY] = this.createCopyClickHandler(
            keywords !== undefined && keywords.length ? keywords.join('\n') : ''
        );

        return (
            <div className={Bem.cls(BLOCK, 'content')}>
                <div className={Bem.cls(BLOCK, 'header')}>
                    <div className={Bem.cls(BLOCK, 'title')}>{i18n('keywords-title')}</div>
                    {keywords && keywords.length > 0 && this.getIcons(true, KEY, this.clickHandlers[KEY])}
                </div>
                <div className={Bem.cls(BLOCK, 'keywords-content')}>
                    {keywords && keywords.length > 0 ? this.getKeywords(keywords) : i18n('no-items')}
                </div>
            </div>
        );
    };

    private getKeywords = (keywords: string[]) => {
        const { keywordsTextTypeView } = this.props;
        const separator = keywordsTextTypeView === KeywordsTextTypeView.vertical ? '\n' : ' · ';

        return keywords.join(separator);
    };

    private getIcons = (isKeywords: boolean, copyRefKey: string, onCopyClick?: () => void) => {
        const { keywordsTextTypeView, copyTooltipStatus } = this.props;

        return (
            <div className={Bem.cls(BLOCK, 'icons')}>
                {isKeywords && (
                    <Shape2
                        width={16}
                        height={16}
                        icon={Shape2Icon.shortText}
                        cls={Bem.cls({
                            block: BLOCK,
                            elem: 'icon',
                            mods: {
                                selected: keywordsTextTypeView === KeywordsTextTypeView.vertical ? 'yes' : 'no'
                            }
                        })}
                        onClick={this.onVerticalIconClick}
                    />
                )}
                {isKeywords && (
                    <Shape2
                        width={16}
                        height={16}
                        icon={Shape2Icon.fullText}
                        cls={Bem.cls({
                            block: BLOCK,
                            elem: 'icon',
                            mods: {
                                selected: keywordsTextTypeView === KeywordsTextTypeView.horizontal ? 'yes' : 'no'
                            }
                        })}
                        onClick={this.onHorizontalIconClick}
                    />
                )}
                <Shape2
                    ref={ref => (this.tooltipRefs[copyRefKey] = ref)}
                    width={16}
                    height={16}
                    icon={Shape2Icon.copyNew}
                    cls={Bem.cls({
                        block: BLOCK,
                        elem: 'icon',
                        mods: {
                            type: 'copy'
                        }
                    })}
                    onClick={onCopyClick}
                />
                <ScopedTooltip
                    anchor={this.tooltipRefs[copyRefKey]}
                    theme={copyTooltipStatus === CopyTooltipStatus.error ? 'error' : 'normal'}
                    view={'classic'}
                    tone={'default'}
                    to={['right', 'left']}
                    size={'s'}
                    visible={copyTooltipStatus !== CopyTooltipStatus.hidden}
                >
                    {copyTooltipStatus === CopyTooltipStatus.error ? i18n('copy-error') : i18n('copied')}
                </ScopedTooltip>
            </div>
        );
    };

    private getRetargetingContent = () => {
        const { retargeting, isRetargetingEnabled } = this.props;

        if (!isRetargetingEnabled) {
            return null;
        }

        return (
            <div className={Bem.cls(BLOCK, 'content')}>
                <div className={Bem.cls(BLOCK, 'header')}>
                    <div className={Bem.cls(BLOCK, 'title')}>{i18n('retargeting-title')}</div>
                </div>
                <div className={Bem.cls(BLOCK, 'retargeting-content')}>
                    {retargeting && retargeting.length > 0 ? this.getValueHint(retargeting) : i18n('no-items')}
                </div>
            </div>
        );
    };

    private getUserProfileContent = () => {
        const { isUserProfileEnabled, userProfile } = this.props;

        if (!isUserProfileEnabled) {
            return null;
        }

        const KEY = 'user-profile';

        if (userProfile) {
            this.clickHandlers[KEY] = this.createCopyClickHandler(
                `${userProfile.name.trim()}\n\n${userProfile.userProfileConditions
                    .map(
                        (conditions, idx) =>
                            `${idx + 1}. ${conditions
                                .map(
                                    ({ key, type, values, interestType }) =>
                                        `${getUserProfileConditionTitle(key, type, interestType)}: ${values.join('')}`
                                )
                                .join(' · ')}`
                    )
                    .join('\n')}`
            );
        }

        const items = userProfile ? userProfile.userProfileConditions : [];

        let content;
        if (items.length <= USER_PROFILE_SHORT_VIEW_LENGTH) {
            content = this.getUserProfileList(items);
        } else {
            content = (
                <ExpandableText
                    content={this.getUserProfileList(items)}
                    shortContent={this.getUserProfileList(items.slice(0, USER_PROFILE_SHORT_VIEW_LENGTH))}
                    openedStateSwitcherText={i18n('hide-full-value')}
                    closedStateSwitcherText={i18n('show-full-value')}
                />
            );
        }

        return (
            <div className={Bem.cls(BLOCK, 'content')}>
                <div className={Bem.cls(BLOCK, 'header')}>
                    <div className={Bem.cls(BLOCK, 'title')}>{i18n('user-profile-title')}</div>
                    {userProfile && userProfile.name ? this.getIcons(false, KEY, this.clickHandlers[KEY]) : null}
                </div>
                <div className={Bem.cls(BLOCK, 'user-profile-name')}>
                    {(userProfile && userProfile.name) || i18n('no-item')}
                </div>
                {content}
            </div>
        );
    };

    private getValueHint = (retargeting: string[]) => {
        if (retargeting.length <= RETARGETING_SHORT_VIEW_LENGTH) {
            return this.getRetargetingList(retargeting);
        }

        return (
            <ExpandableText
                content={this.getRetargetingList(retargeting)}
                shortContent={this.getRetargetingList(retargeting.slice(0, RETARGETING_SHORT_VIEW_LENGTH))}
                openedStateSwitcherText={i18n('hide-full-value')}
                closedStateSwitcherText={i18n('show-full-value')}
            />
        );
    };

    private getUserProfileList = (items: UserProfileCondition[][]) => {
        return (
            <ol className={Bem.cls(BLOCK, 'value-list')}>
                {items.map((conditions, idxKey) => {
                    return (
                        <li className={Bem.cls(BLOCK, 'user-profile-condition-item')} key={idxKey}>
                            {conditions.map(({ key, type, values, interestType }, idx) => {
                                return (
                                    <span className={Bem.cls(BLOCK, 'value-list-item')} key={key + idx}>
                                        <span className={Bem.cls(BLOCK, 'value-list-item-title')}>
                                            {`${getUserProfileConditionTitle(key, type, interestType)}:`}
                                        </span>
                                        <span className={Bem.cls(BLOCK, 'value-list-item-text')}>
                                            {values.join(', ')}
                                        </span>
                                        {idx !== conditions.length - 1 && (
                                            <span className={Bem.cls(BLOCK, 'value-list-splitter')}>{' · '}</span>
                                        )}
                                    </span>
                                );
                            })}
                        </li>
                    );
                })}
            </ol>
        );
    };

    private getRetargetingList = (items: string[]) => {
        return (
            <ol className={Bem.cls(BLOCK, 'value-list')}>
                {items.map(name => (
                    <li className={Bem.cls(BLOCK, 'value-list-item')}>
                        <span className={Bem.cls(BLOCK, 'value-list-item-text')}>{name}</span>
                    </li>
                ))}
            </ol>
        );
    };

    private getSkeleton = () => {
        return (
            <div className={Bem.cls(BLOCK, 'content')}>
                <div
                    className={Bem.cls(BLOCK, 'skeleton', {
                        size: 'large'
                    })}
                >
                    <Skeleton isAnimating={true} height={14} />
                </div>
                <div
                    className={Bem.cls(BLOCK, 'skeleton', {
                        size: 'small'
                    })}
                >
                    <Skeleton isAnimating={true} height={14} />
                </div>
                <div
                    className={Bem.cls(BLOCK, 'skeleton', {
                        size: 'medium'
                    })}
                >
                    <Skeleton isAnimating={true} height={14} />
                </div>
            </div>
        );
    };

    private getScreensContent = () => {
        const { isScreensEnabled, placements } = this.props;

        if (!isScreensEnabled) {
            return null;
        }

        const items: Array<[string, Array<string | number>]> = placements
            ? new Array<[string, Array<string | number>]>(
                  [i18n('regions'), placements.regions],
                  [i18n('formats'), placements.formats],
                  [i18n('categories'), placements.facilities],
                  [i18n('operators'), placements.operators],
                  [i18n('zone-categories'), placements.zones],
                  [i18n('screens'), [placements.screensCount]]
              ).filter(([, value]) => value.length)
            : [];

        return (
            <div className={Bem.cls(BLOCK, 'content')}>
                <div className={Bem.cls(BLOCK, 'header')}>
                    <div className={Bem.cls(BLOCK, 'title')}>{i18n('screens-title')}</div>
                </div>
                <div className={Bem.cls(BLOCK, 'screens-content')}>
                    {items.length ? (
                        <ol className={Bem.cls(BLOCK, 'value-list')}>
                            {items.map(([title, value]) => (
                                <li key={title} className={Bem.cls('value-list-item')}>
                                    <span className={Bem.cls(BLOCK, 'values-list-item-title')}>{title}</span>
                                    <span className={Bem.cls(BLOCK, 'value-list-item-text')}>{value.join(', ')}</span>
                                </li>
                            ))}
                        </ol>
                    ) : null}
                </div>
            </div>
        );
    };

    private onHorizontalIconClick = () => {
        return this.props.onChangeTextTypeView(KeywordsTextTypeView.horizontal);
    };

    private onVerticalIconClick = () => {
        return this.props.onChangeTextTypeView(KeywordsTextTypeView.vertical);
    };

    private createCopyClickHandler = (text: string) => () => {
        const { onCopyClick } = this.props;
        return onCopyClick(text);
    };
}
